'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Swal from 'sweetalert2';
import { registerUser, sendConfirmationEmail, sendNotifEmail } from '@/actions/server/users';
import { SocialIcon } from 'react-social-icons';
import { FaSkype, FaPhone } from 'react-icons/fa';
import {
  DollarSign,
  Award,
  TrendingUp,
  RefreshCw,
  Users,
  BarChart2,
  Megaphone,
  Share2,
} from 'lucide-react';
import { resolveAuthTheme } from '@/utils/domainBranding';
import styles from '@/styles/auth/login.module.css';
import ls from '@/styles/auth/leadscale.module.css';
import {
  LeadScaleBrandWordmark,
  SignalStage,
} from '@/components/leadscale/SignalStage';

/**
 * @param {{ initialHostname?: string, registerRole?: 'partner' | 'advertiser' | null }} props
 */
export default function RegisterClient({ initialHostname = '', registerRole = null }) {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    contact_info: '',
    contact_type: '',
    acceptTerms: true,
    showPassword: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [hostname, setHostname] = useState(initialHostname);
  /** Shared /register page: user picks Affiliate vs Advertiser */
  const [chosenRole, setChosenRole] = useState(null);

  const search =
    typeof window !== 'undefined'
      ? Object.fromEntries(new URLSearchParams(window.location.search))
      : {};

  /** Deep-link preselect: ?role=affiliate|partner|advertiser or ?affiliate=1 / ?advertiser=1 */
  const roleFromQuery = (() => {
    const role = String(search.role || '').toLowerCase();
    if (role === 'advertiser' || role === 'adv') return 'advertiser';
    if (role === 'affiliate' || role === 'partner' || role === 'aff') return 'partner';
    if (search.advertiser === '1') return 'advertiser';
    if (search.affiliate === '1' || search.partner === '1') return 'partner';
    return null;
  })();

  // /register always shows role radios; dedicated paths set registerRole
  const isSharedChooser = !registerRole;
  const effectiveRole = registerRole || chosenRole || roleFromQuery;

  useEffect(() => {
    if (typeof window !== 'undefined' && window.location.hostname) {
      setHostname(window.location.hostname);
    }
  }, []);

  useEffect(() => {
    if (registerRole) return;
    if (roleFromQuery) setChosenRole(roleFromQuery);
  }, [registerRole, roleFromQuery]);

  const { branding, isAfflandTheme, isLeadScaleTheme } = resolveAuthTheme(hostname, {
    get: (k) => search[k] ?? null,
  });

  const isAfflandParam = search.affland === '1';
  const isLeadScaleParam = search.leadscale === '1' || search.advertiser === '1';
  const themeQuery = isAfflandParam
    ? '?affland=1'
    : isLeadScaleParam
      ? '?leadscale=1'
      : '';

  const selectRole = (role) => {
    setChosenRole(role);
    if (typeof window === 'undefined' || registerRole) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('affiliate');
    url.searchParams.delete('partner');
    url.searchParams.delete('advertiser');
    url.searchParams.set('role', role === 'advertiser' ? 'advertiser' : 'affiliate');
    window.history.replaceState({}, '', url.pathname + url.search);
  };

  const roleId =
    effectiveRole === 'advertiser'
      ? 11
      : effectiveRole === 'partner'
        ? 4
        : isLeadScaleTheme
          ? null
          : 4;

  const registerHref =
    effectiveRole === 'advertiser'
      ? `/register/advertiser${themeQuery}`
      : effectiveRole === 'partner'
        ? `/register/partner${themeQuery}`
        : `/register${themeQuery}`;
  const loginHref = `/login${themeQuery}`;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const toggleShowPassword = () => {
    setFormData({ ...formData, showPassword: !formData.showPassword });
  };

  const generatePassword = () => {
    const randomPassword = Math.random().toString(36).slice(-8);
    setFormData({ ...formData, password: randomPassword });
  };

  const getContactInfoLabel = () => {
    switch (formData.contact_type) {
      case 'telegram':
        return 'Your Telegram account';
      case 'whatsapp':
        return 'Your WhatsApp number';
      case 'skype':
        return 'Your Skype account';
      case 'phone':
        return 'Your Phone number';
      default:
        return 'Your Contact Information';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!roleId) {
      await Swal.fire({
        title: 'Choose a role',
        text: 'Please register as Affiliate or Advertiser from the portal home.',
        icon: 'info',
      });
      return;
    }
    setIsLoading(true);

    try {
      const registrationResult = await registerUser({
        ...formData,
        roleId,
        registerHost: hostname || initialHostname,
        registerSource: isLeadScaleTheme ? 'leadscale' : isAfflandTheme ? 'affland' : 'exodo',
      });

      if (!registrationResult.success) {
        throw new Error(registrationResult.message || 'Registration failed');
      }

      Promise.all([
        sendConfirmationEmail({
          email: formData.email,
          confirm_token: registrationResult.confirm_token,
          linkHost: hostname || initialHostname,
        }),
        sendNotifEmail('Notification: New User Registration Request', {
          id: registrationResult.userId1,
          email: formData.email,
          contact_type: formData.contact_type,
          contact_info: formData.contact_info,
          register_source: isLeadScaleTheme ? 'leadscale' : isAfflandTheme ? 'affland' : 'exodo',
          registerHost: hostname || initialHostname || '',
          roleId,
          roleLabel: roleId === 11 ? 'Advertiser' : 'Partner / Affiliate',
        }),
      ]).catch((err) => {
        console.error('Email sending failed:', err);
      });

      await Swal.fire({
        title: 'Registration Successful!',
        html: `
          <div class="text-left">
            <p class="mb-3">Your account has been created successfully!</p>
            <p class="mb-3">Please check your email for the confirmation link.</p>
            <div class="text-sm text-gray-600">
              <p class="mb-2"><strong>Didn't receive the email?</strong></p>
              <ul class="list-disc list-inside space-y-1">
                <li>Check your spam/junk folder</li>
                <li>Wait a few minutes for delivery</li>
                <li><a href="/register/resend-confirmation${themeQuery}" class="text-blue-600 hover:underline">Click here to resend</a></li>
              </ul>
            </div>
          </div>
        `,
        icon: 'success',
        timer: 8000,
        showConfirmButton: true,
        confirmButtonText: 'Continue',
      });

      {
        const thankYouQs = new URLSearchParams({ id: String(registrationResult.userId1) });
        if (themeQuery === '?affland=1') thankYouQs.set('affland', '1');
        if (themeQuery === '?leadscale=1') thankYouQs.set('leadscale', '1');
        window.location.href = `/register/thankyou?${thankYouQs.toString()}`;
      }
    } catch (err) {
      Swal.fire({
        title: 'Registration Failed',
        text: err.message,
        icon: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const roleTitle =
    roleId === 11
      ? 'Register as Advertiser'
      : roleId === 4
        ? 'Register as Affiliate'
        : 'Create your account';
  const roleChipLabel = roleId === 11 ? 'Advertiser' : roleId === 4 ? 'Affiliate' : 'Choose role';
  const RoleIcon = roleId === 11 ? Megaphone : roleId === 4 ? Share2 : Share2;
  const signalHighlight = roleId === 11 ? 'advertiser' : roleId === 4 ? 'affiliate' : null;
  const showRolePicker = isLeadScaleTheme && isSharedChooser;

  const afflandNav = (
    <ul className={styles.afflandNav}>
      <li>
        <a href="https://affland.id/index.html" target="_blank" rel="noopener noreferrer">
          Home
        </a>
      </li>
      <li>
        <a href="https://affland.id/about.html" target="_blank" rel="noopener noreferrer">
          About us
        </a>
      </li>
      <li>
        <a href="https://affland.id/offer.html" target="_blank" rel="noopener noreferrer">
          Our Offers
        </a>
      </li>
      <li>
        <a href="https://affland.id/contact.html" target="_blank" rel="noopener noreferrer">
          Contact
        </a>
      </li>
      <li>
        <Link href={registerHref} className={styles.active}>
          Registration
        </Link>
      </li>
      <li>
        <Link href={loginHref}>Login</Link>
      </li>
    </ul>
  );

  const afflandMarketingContent = (
    <div className={styles.afflandContentCol}>
      <h2 className={styles.afflandContentTitle}>Work with us to get:</h2>
      <div className={styles.afflandFeatureGrid}>
        <div className={styles.afflandFeatureCard}>
          <DollarSign className={styles.afflandFeatureIcon} size={28} strokeWidth={2} />
          <div className={styles.afflandFeatureText}>
            <strong>High</strong> Payouts
          </div>
        </div>
        <div className={styles.afflandFeatureCard}>
          <Award className={styles.afflandFeatureIcon} size={28} strokeWidth={2} />
          <div className={styles.afflandFeatureText}>
            <strong>Best</strong> author&apos;s offers
          </div>
        </div>
        <div className={styles.afflandFeatureCard}>
          <TrendingUp className={styles.afflandFeatureIcon} size={28} strokeWidth={2} />
          <div className={styles.afflandFeatureText}>
            <strong>Approval rate</strong> 35% +
          </div>
        </div>
        <div className={styles.afflandFeatureCard}>
          <RefreshCw className={styles.afflandFeatureIcon} size={28} strokeWidth={2} />
          <div className={styles.afflandFeatureText}>
            <strong>Regular</strong> Payments
          </div>
        </div>
        <div className={styles.afflandFeatureCard}>
          <Users className={styles.afflandFeatureIcon} size={28} strokeWidth={2} />
          <div className={styles.afflandFeatureText}>
            <strong>Professional</strong> Support
          </div>
        </div>
        <div className={styles.afflandFeatureCard}>
          <BarChart2 className={styles.afflandFeatureIcon} size={28} strokeWidth={2} />
          <div className={styles.afflandFeatureText}>
            <strong>API &</strong> Live Report
          </div>
        </div>
      </div>
      <p className={styles.afflandSlogan}>Get the profit from the first lead</p>
      <p className={styles.afflandSloganSub}>Great payouts are waiting for you !</p>
      <Link href={loginHref} className={styles.afflandRegButton}>
        Login →
      </Link>
    </div>
  );

  const inputClass = isLeadScaleTheme
    ? 'block w-full rounded-md border border-white/20 bg-white/10 py-2 px-3 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#c8ff35] focus:border-[#c8ff35]'
    : isAfflandTheme
      ? 'block w-full rounded-md border border-gray-300 py-2 px-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500'
      : 'block w-full rounded-md border-gray-300 py-2 px-3 text-gray-900';

  const labelClass = isLeadScaleTheme
    ? 'block text-sm font-medium text-slate-200 mb-1'
    : 'block text-sm font-medium text-gray-900 mb-1';

  const formInner = (
    <>
      {isLeadScaleTheme ? (
        <div className="text-center mb-4">
          {showRolePicker ? (
            <>
              <h2 className={ls.cardTitle}>Create your account</h2>
              <p className={ls.rolePickLabel}>Register as</p>
              <div className={ls.rolePickGrid} role="radiogroup" aria-label="Account type">
                <label
                  className={`${ls.rolePickOption} ${
                    effectiveRole === 'partner' ? ls.rolePickOptionSelected : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="register_role"
                    value="partner"
                    checked={effectiveRole === 'partner'}
                    onChange={() => selectRole('partner')}
                  />
                  <span className={ls.rolePickIcon}>
                    <Share2 size={22} strokeWidth={2} />
                  </span>
                  <strong>Affiliate</strong>
                  <span>Traffic &amp; offers</span>
                </label>
                <label
                  className={`${ls.rolePickOption} ${
                    effectiveRole === 'advertiser' ? ls.rolePickOptionSelected : ''
                  }`}
                >
                  <input
                    type="radio"
                    name="register_role"
                    value="advertiser"
                    checked={effectiveRole === 'advertiser'}
                    onChange={() => selectRole('advertiser')}
                  />
                  <span className={ls.rolePickIcon}>
                    <Megaphone size={22} strokeWidth={2} />
                  </span>
                  <strong>Advertiser</strong>
                  <span>Campaigns &amp; leads</span>
                </label>
              </div>
              {roleId ? (
                <p className={ls.cardSub} style={{ marginBottom: 8 }}>
                  {roleId === 11
                    ? 'Advertiser account — use a different email if you also register as affiliate.'
                    : 'Affiliate (partner) account — traffic & offers.'}
                </p>
              ) : (
                <p className={ls.cardSub}>Pick a role to continue.</p>
              )}
            </>
          ) : (
            <>
              <div className={ls.roleIconWrap} aria-hidden="true">
                <RoleIcon size={28} strokeWidth={2} />
              </div>
              <div className={ls.roleChip}>
                <i />
                <span>{roleChipLabel}</span>
              </div>
              <h2 className={ls.cardTitle}>{roleTitle}</h2>
              <p className={ls.cardSub}>
                {roleId === 11
                  ? 'Advertiser account — use a different email if you also register as affiliate.'
                  : 'Affiliate (partner) account — traffic & offers.'}
              </p>
            </>
          )}
        </div>
      ) : (
        <div className="text-center mb-6">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={branding.logo}
            alt="Logo"
            width="200"
            height="80"
            className={`mx-auto ${isAfflandTheme ? styles.afflandLogo : ''}`}
          />
          <h2
            className={`text-2xl font-bold text-gray-900 mb-6 ${isAfflandTheme ? 'text-center' : ''}`}
          >
            {roleTitle}
          </h2>
        </div>
      )}

      <form className="space-y-5" onSubmit={handleSubmit}>
        <div>
          <label htmlFor="email" className={labelClass}>
            Email
          </label>
          <input
            name="email"
            type="email"
            required
            value={formData.email}
            onChange={handleChange}
            className={inputClass}
          />
        </div>

        <div>
          <label htmlFor="password" className={labelClass}>
            Password
          </label>
          <div className="relative">
            <input
              name="password"
              type={formData.showPassword ? 'text' : 'password'}
              required
              value={formData.password}
              onChange={handleChange}
              className={`${inputClass} pr-28`}
            />
            <div className="absolute right-1 top-1/2 -translate-y-1/2 flex gap-1">
              <button
                type="button"
                onClick={generatePassword}
                className={
                  isLeadScaleTheme
                    ? 'bg-white/15 hover:bg-white/25 text-white px-2 py-1 rounded text-xs font-medium'
                    : isAfflandTheme
                      ? styles.afflandSmallBtn
                      : 'bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded text-xs font-medium'
                }
              >
                Generate
              </button>
              <button
                type="button"
                onClick={toggleShowPassword}
                className={
                  isLeadScaleTheme
                    ? 'bg-white/15 hover:bg-white/25 text-white px-2 py-1 rounded text-xs font-medium'
                    : isAfflandTheme
                      ? styles.afflandSmallBtn
                      : 'bg-gray-200 hover:bg-gray-300 px-2 py-1 rounded text-xs font-medium'
                }
              >
                {formData.showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
        </div>

        <div>
          <label className={`${labelClass} mb-2`}>Contact</label>
          <div className="flex flex-wrap gap-4">
            {[
              {
                value: 'telegram',
                icon: <SocialIcon network="telegram" style={{ height: 22, width: 22 }} />,
                label: 'Telegram',
              },
              {
                value: 'whatsapp',
                icon: <SocialIcon network="whatsapp" style={{ height: 22, width: 22 }} />,
                label: 'WhatsApp',
              },
              {
                value: 'skype',
                icon: <FaSkype style={{ height: 22, width: 22 }} />,
                label: 'Skype',
              },
              {
                value: 'phone',
                icon: <FaPhone style={{ height: 22, width: 22 }} />,
                label: 'Phone',
              },
            ].map(({ value, icon, label }) => (
              <label
                key={value}
                className={`inline-flex items-center cursor-pointer ${
                  isLeadScaleTheme ? 'text-slate-200' : isAfflandTheme ? styles.afflandRadioLabel : ''
                }`}
              >
                <input
                  type="radio"
                  name="contact_type"
                  value={value}
                  required
                  checked={formData.contact_type === value}
                  onChange={handleChange}
                  className="mr-2"
                />
                {icon}
                <span className="ml-2">{label}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className={labelClass}>{getContactInfoLabel()}</label>
          <input
            name="contact_info"
            type="text"
            required
            value={formData.contact_info}
            onChange={handleChange}
            className={inputClass}
          />
        </div>

        <div className="flex items-center hidden">
          <input
            type="checkbox"
            name="acceptTerms"
            checked={true}
            onChange={handleChange}
            className="h-4 w-4 rounded"
          />
          <label className="ml-2 text-sm text-gray-900">
            I accept{' '}
            <a
              href="/register/terms-and-conditions"
              target="_blank"
              className={isAfflandTheme ? styles.afflandLink : 'text-blue-600 hover:underline'}
            >
              terms & conditions
            </a>
          </label>
        </div>

        <div>
          <button
            type="submit"
            disabled={isLoading || !roleId}
            className={
              isLeadScaleTheme
                ? ls.buttonPrimary
                : isAfflandTheme
                  ? styles.afflandButton
                  : `w-full py-2 px-4 rounded transition-all ${
                      isLoading
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                    }`
            }
          >
            {isLoading ? 'Creating your account...' : 'Register'}
          </button>
        </div>
      </form>

      <div className="mt-4 text-center space-y-2">
        <Link
          href={loginHref}
          className={
            isLeadScaleTheme
              ? ls.link
              : isAfflandTheme
                ? styles.afflandLinkItem
                : 'text-sm text-indigo-600 hover:underline'
          }
        >
          Already have an account? Go to login
        </Link>
        {isLeadScaleTheme && !showRolePicker && roleId === 4 ? (
          <div>
            <Link href={`/register/advertiser${themeQuery}`} className={ls.link}>
              Need an advertiser account instead?
            </Link>
          </div>
        ) : null}
        {isLeadScaleTheme && !showRolePicker && roleId === 11 ? (
          <div>
            <Link href={`/register/partner${themeQuery}`} className={ls.link}>
              Need an affiliate account instead?
            </Link>
          </div>
        ) : null}
      </div>
    </>
  );

  if (isLeadScaleTheme) {
    return (
      <div className={ls.page}>
        <div className={ls.pageGrid} />
        <header className={ls.header}>
          <LeadScaleBrandWordmark />
          <div className={ls.headerLinks}>
            <Link href="/">Home</Link>
            <Link href={loginHref}>Login</Link>
          </div>
        </header>
        <div className={ls.authSplit}>
          <div className={ls.card}>{formInner}</div>
          <div className={ls.authVisual} aria-hidden="true">
            <SignalStage highlight={signalHighlight} />
          </div>
        </div>
        <footer className={ls.footer}>© LeadScale Global Partner</footer>
      </div>
    );
  }

  if (isAfflandTheme) {
    return (
      <div className={styles.afflandLoginPage}>
        <header className={styles.afflandHeader}>
          <nav>{afflandNav}</nav>
        </header>
        <main className={styles.afflandMain}>
          <div className={styles.afflandMainInner}>
            <div className={styles.afflandFormCol}>
              <div className={styles.afflandCard}>{formInner}</div>
            </div>
            {afflandMarketingContent}
          </div>
        </main>
        <footer className={styles.afflandFooter}>
          <span>
            © 2026 Affland by EXODO. All rights reserved.{' '}
            <Link href="/privacy-policy">Privacy Policy</Link>
          </span>
        </footer>
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-1 flex-col justify-center py-12 sm:px-6 lg:px-8 bg-gray-100">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white px-6 py-12 shadow sm:rounded-lg sm:px-10">{formInner}</div>
      </div>
    </div>
  );
}
